import { createContext, useState, ReactNode } from 'react';
import { useReprocessRuns } from '../hooks/useRuns';
import { Run } from '../types/Run';
import { Session } from '../types/Session';
import { Config } from '../types/Config';

import { createConfig } from '../api/configs';

type ReprocessStatus = 'idle' | 'processing' | 'success' | 'error';
type AutoCalculateStatus = 'idle' | 'processing' | 'success' | 'error';

interface ReprocessContextType {
  sessionsToReprocess: Session[];
  configsToReprocess: Config[];
  runsToReprocess: Run[];
  shouldReprocess: boolean;
  reprocessStatus: ReprocessStatus;
  autoCalculateStatus: AutoCalculateStatus;
  isProcessing: boolean;
  isAutoCalculating: boolean;
  addSessionToReprocess: (session: Session) => void;
  addConfigToReprocess: (config: Config) => void;
  addRunToReprocess: (runs: Run[]) => void;
  removeSessionFromReprocess: (sessionId: string) => void;
  removeConfigFromReprocess: (configId: string) => void;
  removeRunFromReprocess: (runId: string) => void;
  removeAllReprocess: () => void;
  handleReprocess: (
    invalidateSession: () => void,
    immediateRuns?: Run[],
  ) => Promise<void>;
  handleAutoCalculate: (session: Session, invalidateSession: () => void) => Promise<void>;
  resetStatus: () => void;
  resetAutoCalculateStatus: () => void;
}

const ReprocessContext = createContext<ReprocessContextType | null>(null);

interface ReprocessProviderProps {
  children: ReactNode;
  autoHideSuccess?: boolean;
  autoHideDelay?: number;
}

/**
 * Provider component that manages global reprocess state.
 * Ensures only one reprocess operation can run at a time across the entire application.
 *
 * @param children - React components to wrap
 * @param autoHideSuccess - Whether to auto-hide success messages (default: true)
 * @param autoHideDelay - Delay before auto-hiding success messages in ms (default: 5000)
 *
 * @example
 * ```tsx
 * // In your App.tsx or main component
 * <ReprocessProvider>
 *   <YourApp />
 * </ReprocessProvider>
 * ```
 */
export const ReprocessProvider = ({
  children,
  autoHideSuccess = true,
  autoHideDelay = 5000,
}: ReprocessProviderProps) => {
  const [sessionsToReprocess, setSessionsToReprocess] = useState<Session[]>([]);
  const [configsToReprocess, setConfigsToReprocess] = useState<Config[]>([]);
  const [runsToReprocess, setRunsToReprocess] = useState<Run[]>([]);
  const [reprocessStatus, setReprocessStatus] = useState<ReprocessStatus>('idle');
  const [autoCalculateStatus, setAutoCalculateStatus] = useState<AutoCalculateStatus>('idle');
  const reprocessRunsMutation = useReprocessRuns();

  // Computed property to determine if we should reprocess
  const shouldReprocess = sessionsToReprocess.length > 0 || configsToReprocess.length > 0 || runsToReprocess.length > 0;

  const addSessionToReprocess = (session: Session) => {
    setSessionsToReprocess((prev) => {
      // Check if session is already in the list
      if (prev.some((s) => s.id === session.id)) {
        return prev;
      }
      return [...prev, session];
    });
  };

  const addConfigToReprocess = (config: Config) => {
    setConfigsToReprocess((prev) => {
      // Check if config is already in the list
      if (prev.some((c) => c.id === config.id)) {
        return prev;
      }
      return [...prev, config];
    });
  };

  const addRunToReprocess = (runs: Run[]) => {
    setRunsToReprocess((prev) => {
      const newRuns = [...prev];
      runs.forEach((run) => {
        // Check if run is already in the list
        if (!newRuns.some((r) => r.id === run.id)) {
          newRuns.push(run);
        }
      });
      return newRuns;
    });
  };

  const removeSessionFromReprocess = (sessionId: string) => {
    setSessionsToReprocess((prev) => prev.filter((s) => s.id !== sessionId));
  };

  const removeConfigFromReprocess = (configId: string) => {
    setConfigsToReprocess((prev) => prev.filter((c) => c.id !== configId));
  };

  const removeRunFromReprocess = (runId: string) => {
    setRunsToReprocess((prev) => prev.filter((r) => r.id !== runId));
  };

  const removeAllReprocess = () => {
    setSessionsToReprocess([]);
    setConfigsToReprocess([]);
    setRunsToReprocess([]);
  };

  const handleReprocess = async (
    invalidateSession: () => void,
    immediateRuns?: Run[],
  ) => {
    // Convert all sessions, configs, and runs to a flat array of runs
    const allRunsToReprocess: Run[] = [
      ...(immediateRuns || []),
      ...runsToReprocess,
      ...sessionsToReprocess.flatMap((session) => session.configs.flatMap((config) => config.runs)),
      ...configsToReprocess.flatMap((config) => config.runs),
    ];

    if (allRunsToReprocess.length === 0) {
      return;
    }

    try {
      setReprocessStatus('processing');
      await reprocessRunsMutation.mutateAsync({
        runsToReprocess: allRunsToReprocess,
      });
      setReprocessStatus('success');

      // Clear all reprocess lists after successful reprocessing
      removeAllReprocess();

      invalidateSession();

      // Auto-hide success message if enabled
      if (autoHideSuccess) {
        setTimeout(() => {
          setReprocessStatus('idle');
        }, autoHideDelay);
      }
    } catch (e) {
      console.error(e);
      setReprocessStatus('error');
    }
  };

  const handleAutoCalculate = async (session: Session, invalidateSession: () => void) => {
    setAutoCalculateStatus('processing');
    try {
      await createConfig(
        JSON.stringify({
          team_id: session.team_id,
          testsession_id: session.id,
          autocalibrate: true,
        }),
      );
      setAutoCalculateStatus('success');
      invalidateSession();

      // Auto-hide success message if enabled
      if (autoHideSuccess) {
        setTimeout(() => {
          setAutoCalculateStatus('idle');
        }, autoHideDelay);
      }
    } catch (e) {
      console.error(e);
      setAutoCalculateStatus('error');
    }
  };

  const resetStatus = () => {
    setReprocessStatus('idle');
  };

  const resetAutoCalculateStatus = () => {
    setAutoCalculateStatus('idle');
  };

  const value: ReprocessContextType = {
    sessionsToReprocess,
    configsToReprocess,
    runsToReprocess,
    shouldReprocess,
    reprocessStatus,
    autoCalculateStatus,
    isProcessing: reprocessStatus === 'processing',
    isAutoCalculating: autoCalculateStatus === 'processing',
    addSessionToReprocess,
    addConfigToReprocess,
    addRunToReprocess,
    removeSessionFromReprocess,
    removeConfigFromReprocess,
    removeRunFromReprocess,
    removeAllReprocess,
    handleReprocess,
    handleAutoCalculate,
    resetStatus,
    resetAutoCalculateStatus,
  };

  return <ReprocessContext.Provider value={value}>{children}</ReprocessContext.Provider>;
};

export { ReprocessContext };
export type { ReprocessContextType, ReprocessStatus, AutoCalculateStatus };
